import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import SendComposeModal from './SendComposeModal';
import { apiErrorText, apiRequest } from '../../utils';

/* What the organiser of somebody ELSE's event needs from each of our participants.

   An external event (a trade fair, a conference, a charity run) is one we do not run. Our people
   register with us, and then the attendee list has to reach that organiser — who wants more than
   a name: a number to reach somebody on the day, a date of birth for the age category, a t-shirt
   size. This block is where a host says what that is, once, at the moment they tick "somebody
   else runs this".

   ★ THE QUESTION SPLITS IN TWO, AND THE BLOCK SHOWS IT. Some of what an organiser asks for we
   already hold on the person (mobile, date of birth, job title) — asking again would be rude and
   would give the export two sources for one fact, so those are TICKS and become columns. The
   rest we do not know (t-shirt size, dietary needs), and those have to be ASKED: they become real
   questions in the event's pre-survey, answered at registration.

   ★ The questions half is an ADD list, not a list of toggles. From the second a template is
   pressed it is an ordinary survey question — renamed, re-optioned, reordered or deleted in the
   survey editor that already exists. A tick that had to un-write one would have to find it back
   by its text, and would break the moment somebody rewrote the wording. So: add here, edit there,
   and the link says so.

   ★ The ticks SAVE AS YOU TICK, unlike the form around them. There is no draft of "which data do
   we hand over" worth keeping — and the alternative, folding them into the editor's Save, means a
   host who ticks three boxes and navigates away has silently chosen nothing.

   ★ Nothing here mails anybody by itself. Adding a question after people have already registered
   is the one moment somebody needs to know, and the platform already has that message — the
   survey request, the sixth event mail, with its own composer in front of it. That is the button
   at the bottom; there is no seventh message. */

// Which of the builder's field types a host may pick for a question of their own. Deliberately
// short: these are the ones that mean something on an intake form and that every renderer,
// including Machina's event editor, can draw. The starter templates may use more.
const CUSTOM_TYPES = ['shorttext', 'longtext', 'number', 'yesno', 'phone', 'date'];

const OrganiserNeeds = ({ eventId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('shorttext');
  // Which survey the "ask them" composer is aimed at, or null. A question list can span more
  // than one set, so the button asks per set rather than pretending there is only one.
  const [asking, setAsking] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/data-fields`)
      .then(setData)
      // Silent: this is one block on a screen that is useful without it, and the gate behind it
      // is narrower than the gate on the editor itself.
      .catch(() => setData(null));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    if (busy || !data) return;
    const keys = data.profile.filter((f) => (f.key === key ? !f.picked : f.picked))
      .map((f) => f.key);
    setBusy(true);
    setNotice(null);
    apiRequest('PUT', `/company/event/${eventId}/data-fields`, { keys })
      .then((res) => setData((d) => ({ ...d, profile: res.profile })))
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const addQuestion = (body) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/data-fields/questions`, body)
      .then((res) => {
        setData((d) => ({ ...d, questions: res.questions }));
        setLabel('');
        setAdding(false);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  if (data === null) return null;

  const questions = data.questions || [];
  // One entry per set the questions live in — what the "ask them" buttons send for.
  const surveys = [];
  questions.forEach((q) => {
    if (!surveys.some((s) => s.id === q.survey_id)) {
      surveys.push({ id: q.survey_id, name: q.survey_name });
    }
  });

  return (
    <>
      {asking && (
        <SendComposeModal
          eventId={eventId}
          kind="survey"
          segment="survey_pending"
          surveyId={asking.id}
          title={t('askTheRegistered')}
          sendPath={`/company/event/${eventId}/surveys/${asking.id}/remind`}
          onClose={() => setAsking(null)}
          onSent={(res) => {
            setNotice(t('sendSummary', { mailed: res.mailed || 0, n: res.notified || 0 }));
            setAsking(null);
          }}
        />
      )}

      <p className="ce-note">{t('organiserNeedsHint')}</p>

      {/* --- the half we already hold -------------------------------------------------- */}
      <div className="ce-group">
        <span className="bravos-form__label">{t('weAlreadyHaveThis')}</span>
        <p className="ce-note">{t('weAlreadyHaveThisHint')}</p>
        <div className="ce-needs">
          {data.profile.map((field) => (
            <label key={field.key} className="ce-check">
              <input
                type="checkbox"
                checked={field.picked}
                disabled={busy}
                onChange={() => toggle(field.key)}
              />
              <span>
                {t(field.label_key)}
                {/* Named, not hidden: these are the columns the export's "leave the personal
                    details out" switch drops, and knowing which ones those are BEFORE you
                    press send is the whole value of the switch. */}
                {field.is_personal && (
                  <span className="ce-pill ce-pill--draft ms-2">{t('personalData')}</span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* --- the half we have to ask ----------------------------------------------------- */}
      <div className="ce-group">
        <span className="bravos-form__label">{t('weHaveToAskThis')}</span>
        <p className="ce-note">{t('weHaveToAskThisHint')}</p>

        {questions.length === 0 ? (
          <p className="ce-note">{t('nothingAskedYet')}</p>
        ) : (
          questions.map((q) => (
            <div key={q.id} className="ce-needs__q">
              <span className="ce-needs__qtext">
                {q.text}
                {q.is_required && <span className="ce-needs__req"> *</span>}
              </span>
              {q.options.length > 0 && (
                <span className="ce-needs__opts">{q.options.join(' · ')}</span>
              )}
            </div>
          ))
        )}

        <p className="bravos-cal__label">{t('addAQuestion')}</p>
        <div className="ce-tabs">
          {(data.templates || []).map((tpl) => (
            <button
              key={tpl.key}
              type="button"
              className="ce-tab"
              disabled={busy}
              onClick={() => addQuestion({ template: tpl.key })}
            >
              <Icon name="plus" /> {t(tpl.label_key)}
            </button>
          ))}
          <button
            type="button"
            className="ce-tab"
            disabled={busy}
            onClick={() => setAdding((v) => !v)}
          >
            <Icon name="pencil" /> {t('ownQuestion')}
          </button>
        </div>

        {adding && (
          <div className="ce-needs__custom">
            <input
              type="text"
              value={label}
              maxLength={250}
              placeholder={t('questionText')}
              onChange={(e) => setLabel(e.target.value)}
            />
            <select value={fieldType} onChange={(e) => setFieldType(e.target.value)}>
              {CUSTOM_TYPES.map((type) => (
                <option key={type} value={type}>{t(`fieldType_${type}`)}</option>
              ))}
            </select>
            <button
              type="button"
              className="bravos-copy"
              disabled={busy || !label.trim()}
              onClick={() => addQuestion({ label: label.trim(), field_type: fieldType })}
            >
              {t('add')}
            </button>
          </div>
        )}

        {questions.length > 0 && (
          <div className="bravos-form__row mt-2">
            <button
              type="button"
              className="bravos-copy"
              onClick={() => navigate('/company-surveys')}
            >
              <Icon name="pencil" /> {t('editTheQuestions')}
            </button>
            {/* ★ Only worth offering once there is something to answer. It reaches the people who
                registered and have NOT answered — the server's default segment — which after a
                question is added late is exactly everybody. */}
            {surveys.map((survey) => (
              <button
                key={survey.id}
                type="button"
                className="bravos-copy"
                onClick={() => setAsking(survey)}
              >
                <Icon name="bell" />
                {' '}
                {surveys.length > 1
                  ? t('askTheRegisteredFor', { name: survey.name })
                  : t('askTheRegistered')}
              </button>
            ))}
          </div>
        )}
      </div>

      {notice && <p className="bravos-cal__notice">{notice}</p>}
    </>
  );
};

export default OrganiserNeeds;
